import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import { composeUri } from '@/utils/compose';
import { AVATARS, GROUP_USERS, SITES_METADATA_AVATAR_TYPE_NAME } from '@/utils/CONST';
import { CancelOutlined, LocationOnOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import LoadingButton from '@mui/lab/LoadingButton';
import { useQueryClient } from '@tanstack/react-query';
import { flatten, uniq } from 'lodash';
import { RefreshButton, TopToolbar } from 'ra-ui-materialui';
import { useState } from 'react';
import {
    Button,
    DataTable,
    FilterButton,
    List,
    TextInput,
    useNotify,
    useRecordSelection,
    useTranslate,
    useUpdate,
} from 'react-admin';
import { Outlet, useNavigate, useParams } from 'react-router';

const SitesToAgencyListDialog = () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    const { agencyId } = useParams();
    const [update, { isLoading }] = useUpdate();
    const notify = useNotify();
    const queryClient = useQueryClient();
    const [multiple, setMultiple] = useState(false);
    const [selectedSites, { clearSelection }] = useRecordSelection({ resource: AVATARS, storeKey: 'compactSitesList' });

    const navigateBack = () => {
        navigate(-1);
    };

    const handleAddMultipleToAgency = () => {
        const sitesFromCache = selectedSites
            .map((siteUriId) => {
                const siteData = queryClient.getQueryData([AVATARS, 'getOne', { id: siteUriId }]) as any;
                return siteData;
            })
            .filter((site) => site !== undefined);

        const allGroupAvatars = flatten(
            sitesFromCache.map((site) => site?.groupAvatars || []).filter((groups) => groups.length > 0),
        );

        const uniqueGroupAvatars = uniq(allGroupAvatars);

        update(
            GROUP_USERS,
            {
                id: `${composeUri(GROUP_USERS, agencyId)}/add_remove_group_avatars`,
                data: {
                    groupAvatars: uniqueGroupAvatars,
                    add: true,
                },
                meta: {
                    api: 'v2',
                },
            },
            {
                onSuccess: () => {
                    notify('agency.site_added', { type: 'success', messageArgs: { smart_count: selectedSites.length } });
                    queryClient.invalidateQueries({ queryKey: [AVATARS] });
                    queryClient.invalidateQueries({ queryKey: [GROUP_USERS] });
                    clearSelection();
                    setMultiple(false);
                },
                onError: () => {
                    notify('errorMessages.fail_operation', { type: 'error' });
                },
            },
        );
    };

    return (
        <DialogTemplate
            data-cy={'compact-sites-list-dialog'}
            maxWidth={'md'}
            icon={<LocationOnOutlined />}
            open={true}
            onClose={navigateBack}
            title={translate('agency.list.sites.link_sites')}
        >
            <ListStylesContainer>
                <List
                    actions={
                        <TopToolbar sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterButton />

                            <Button
                                startIcon={multiple ? <CancelOutlined /> : <AddIcon />}
                                label={multiple ? 'ra.action.cancel' : 'agency.list.sites.add'}
                                onClick={() => {
                                    setMultiple(!multiple);
                                    clearSelection();
                                }}
                            />

                            <RefreshButton
                                data-cy='refresh-button'
                                style={{
                                    paddingLeft: '12px',
                                    paddingRight: '12px',
                                }}
                            />
                        </TopToolbar>
                    }
                    component='div'
                    resource={AVATARS}
                    filter={{
                        'metadataAvatarType.name': SITES_METADATA_AVATAR_TYPE_NAME,
                        enabled: true,
                    }}
                    sort={{
                        field: 'creationDate',
                        order: 'ASC',
                    }}
                    empty={false}
                    storeKey='compactSitesList'
                    filters={[
                        <TextInput
                            source='name'
                            label={'site.form.site_name'}
                            variant='standard'
                            resettable
                            alwaysOn
                        />,
                    ]}
                    disableSyncWithLocation
                    queryOptions={{
                        enabled: !!agencyId,
                    }}
                >
                    <DataTable
                        bulkActionButtons={
                            multiple ? (
                                <LoadingButton loading={isLoading} onClick={handleAddMultipleToAgency}>
                                    {translate('ra.action.confirm')}
                                </LoadingButton>
                            ) : (
                                false
                            )
                        }
                    >
                        <DataTable.Col source='name' label={'site.form.site_name'} />
                    </DataTable>
                </List>
            </ListStylesContainer>

            <Outlet />
        </DialogTemplate>
    );
};

export default SitesToAgencyListDialog;
